import { useContext } from 'react'
import PropTypes from 'prop-types'

import { digObject } from '@domain-group/javascript-utils'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import useProvider, { useProviderForm } from '@hooks/useProvider'

const ManageProviderOptionsModal = (props) => {
  const { callbacks, modalKey, showModal } = props
  const { closeModal, updateProvider } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, modalKey, {})
  const { provider: initProvider } = modalPayload

  const providerPayload = useProvider(initProvider)
  const {
    provider,
    callbacks: {
      updateProvider: updateFn,
    },
    updating,
  } = providerPayload

  const providerFormPayload = useProviderForm(provider)
  const {
    entityState,
    entityState: {
      in_house,
      options,
      notes_enabled,
    },
    handlers,
  } = providerFormPayload

  const {
    auto_generate_invoices,
    bookable,
    content_producer,
    charge_for_package,
    enable_campaign_creation,
    job_ticket_layout,
    order_total_override,
    permit_task_editing,
    preferred_supplier,
    realhub_platform,
    require_identity_code,
    xero_integration,
  } = options

  const updateProviderPayload = {
    callbacks: {
      action: updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: 'Provider Options Updated',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={modalKey} showModal={showModal} size="small">
      <SidebarModal.Header
        callbacks={callbacks}
        title="Provider"
        titleSecondLine="Options"
      />
      <SidebarModal.Content>
        <Form>
          <Form.Field label="Campaign Creation">
            <select
              data-value-type="boolean"
              name="options.enable_campaign_creation"
              value={enable_campaign_creation}
              {...handlers}
            >
              <option value="false">No</option>
              <option value="true">Yes</option>
            </select>
          </Form.Field>

          <Form.Field marginTop="large" label="Content Producer">
            <select
              data-value-type="boolean"
              name="options.content_producer"
              value={content_producer}
              {...handlers}
            >
              <option value="false">No</option>
              <option value="true">Yes</option>
            </select>
          </Form.Field>

          <Form.Field marginTop="large" label="Enable Notes">
            <select
              data-value-type="boolean"
              name="notes_enabled"
              value={notes_enabled}
              {...handlers}
            >
              <option value="false">No</option>
              <option value="true">Yes</option>
            </select>
          </Form.Field>

          <Form.Field marginTop="large" label="In-House">
            <select
              name="in_house"
              data-value-type="boolean"
              value={in_house}
              {...handlers}
            >
              <option value="false">No</option>
              <option value="true">Yes</option>
            </select>
          </Form.Field>

          <Form.Field marginTop="large" label="Auto Generate Invoices">
            <select
              data-value-type="boolean"
              name="options.auto_generate_invoices"
              value={auto_generate_invoices}
              {...handlers}
            >
              <option value="">Please Select...</option>
              <option value="false">No</option>
              <option value="true">Yes</option>
            </select>
          </Form.Field>

          <Form.Field marginTop="large" label="Xero">
            <select
              name="options.xero_integration"
              value={xero_integration}
              {...handlers}
            >
              <option value="">Please Select...</option>
              <option value="V2">V2 (Default)</option>
            </select>
          </Form.Field>

          <Form.Field marginTop="large" label="Identity Code">
            <select
              data-value-type="boolean"
              name="options.require_identity_code"
              value={require_identity_code}
              {...handlers}
            >
              <option value="false">Not Required</option>
              <option value="true">Required</option>
            </select>
          </Form.Field>

          <Form.Field marginTop="large" label="Job Ticket Layout">
            <select
              data-value-type="boolean"
              name="options.job_ticket_layout"
              value={job_ticket_layout}
              {...handlers}
            >
              <option value="orders">By Orders</option>
              <option value="order_items">By Order Items</option>
            </select>
          </Form.Field>

          <Form.Field marginTop="large" label="Order Total Override">
            <select
              data-value-type="boolean"
              name="options.order_total_override"
              value={order_total_override}
              {...handlers}
            >
              <option value="false">Off</option>
              <option value="true">On</option>
            </select>
          </Form.Field>

          <Form.Field marginTop="large" label="Preferred Supplier">
            <select
              data-value-type="boolean"
              name="options.preferred_supplier"
              value={preferred_supplier}
              {...handlers}
            >
              <option value="">Please Select...</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </Form.Field>

          <Form.Field marginTop="large" label="Bookable">
            <select
              data-value-type="boolean"
              name="options.bookable"
              value={bookable}
              {...handlers}
            >
              <option value="false">No</option>
              <option value="true">Yes</option>
            </select>
          </Form.Field>

          <Form.Field marginTop="large" label="Permit Task Editing">
            <select
              data-value-type="boolean"
              name="options.permit_task_editing"
              value={permit_task_editing}
              {...handlers}
            >
              <option value="false">No</option>
              <option value="true">Yes</option>
            </select>
          </Form.Field>

          <Form.Field marginTop="large" label="Realhub Platform">
            <select
              name="options.realhub_platform"
              value={realhub_platform}
              {...handlers}
            >
              <option value="legacy">Legacy</option>
              <option value="standalone_v1">Standalone</option>
            </select>
          </Form.Field>

          <Form.Field marginTop="large" label="Charge for Package">
            <select
              data-value-type="boolean"
              name="options.charge_for_package"
              value={charge_for_package}
              {...handlers}
            >
              <option value="false">No</option>
              <option value="true">Yes</option>
            </select>
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          loading={updating}
          onClick={() => updateProvider(updateProviderPayload)}
          size="large"
        >
          Save Provider Options
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

ManageProviderOptionsModal.propTypes = {
  callbacks: PropTypes.object.isRequired,
  modalKey: PropTypes.string,
  showModal: PropTypes.bool,
}

ManageProviderOptionsModal.defaultProps = {
  modalKey: 'ManageProviderOptionsModal',
}

const LazyLoadedModal = props => (
  <SidebarModal.RenderController {...props}>
    <ManageProviderOptionsModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
